import React, { useEffect, useState } from "react";
import { Tag } from "antd";
import axios from "axios";
import { Error } from "../Messages/Messages";
import moment from "moment";
import { CreateNewInventory } from "./CreateInventory";
import { UpdateInventory } from "./UpdateInventory";
import DeleteModal from "../DeleteModal/DeleteModal";
import DropdownComp from "../Dropdowns/DropdownComp";
import GeneralTable from "../GeneralTable";
import { generalErrorAlert } from "../GeneralErrorAlert";
import SearchBarComp from "../SearchBar/SearchBar";
import PageToolbar from "../../design-system/PageToolbar";

import { useTranslation } from "react-i18next";
const MainInventoryList = ({ projectId, milestoneId }) => {
  const { t } = useTranslation("dashboard");
  const [inventory, setInventory] = useState([]);
  const [totalCount, setTotalCount] = useState("");
  const [searchText, setSearchText] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(6);

  const handleSearch = value => {
    setSearchText(value);
    setPageNo(1);
  };

  useEffect(() => {
    getData();
    return () => {};
  }, [projectId, milestoneId, searchText, pageNo]);

  const getData = async () => {
    try {
      await axios
        .post(
          `${process.env.REACT_APP_BACKEND_URL}/inventory/filters`,
          {
            projectId,
            milestoneId,
            searchText,
            pageSize,
            pageNo: pageNo - 1
          },
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token")
            }
          }
        )
        .then(res => {
          if (res.status === 200 || res.status === 304) {
            setInventory(res.data.data);
            setTotalCount(res.data.count);
          } else {
            Error(res.data.errorMessage);
          }
        })
        .catch(err => {
          generalErrorAlert(err);
        });
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const deleteHandler = async id => {
    try {
      await axios
        .delete(`${process.env.REACT_APP_BACKEND_URL}/inventory/delete/${id}`, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        })
        .then(res => {
          if (res.status === 200 || res.status === 304) {
            getData();
          } else {
            Error(res.data.errorMessage);
          }
        })
        .catch(err => {
          generalErrorAlert(err);
        });
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const columns = [
    {
      title: t("Item Name"),
      dataIndex: "itemName",
      mobile: "title",
      sorter: (a, b) => a.itemName.localeCompare(b.itemName)
    },
    {
      title: t("Total"),
      dataIndex: "total",
      sorter: (a, b) => a.total - b.total
    },
    {
      title: t("Used"),
      dataIndex: "used",
      render: (text, record) => record.used || 0,
      sorter: (a, b) => (a.used || 0) - (b.used || 0)
    },
    {
      title: t("Remaining"),
      render: (text, record) => record.total - (record.used || 0),
      sorter: (a, b) => a.total - (a.used || 0) - (b.total - (b.used || 0))
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "subtitle",
      render: (text, record) => {
        const available = record.total - (record.used || 0);
        let status = "IN STOCK";
        if (available <= 0) {
          status = "OUT OF STOCK";
        } else if (record.minimumAmount && available <= record.minimumAmount) {
          status = "LOW STOCK";
        }
        return <Tag color={status !== "IN STOCK" ? "error" : "success"}>{t(status)}</Tag>;
      }
    },
    {
      title: t("Last Updated"),
      dataIndex: "updatedAt",
      mobile: "hidden",
      render: text => moment(text).format("DD/MMM/YYYY")
    },
    {
      title: t("Action"),
      key: "action",
      mobile: "actions",
      render: (text, obj) => {
        const menuItems = [
          {
            key: "view",
            inventory: true
          },
          {
            key: "edit",
            component: () => <UpdateInventory inventory={obj} updateParent={getData} />
          },
          {
            key: "delete",
            component: () => (
              <DeleteModal text="Inventory Item" onClick={() => deleteHandler(obj?._id)} />
            )
          }
        ];
        return (
          <div>
            <DropdownComp menuItems={menuItems} />
          </div>
        );
      }
    }
  ];

  return (
    <div className="inventory-list-page">
      <PageToolbar
        filters={<SearchBarComp handleSearch={handleSearch} />}
        actions={
          <CreateNewInventory
            projectId={projectId}
            milestoneId={milestoneId}
            updateParent={getData}
          />
        }
      />
      <GeneralTable
        pageNo={pageNo}
        pageSize={pageSize}
        totalCount={totalCount}
        data={inventory}
        columns={columns}
        setPageNo={setPageNo}
      />
    </div>
  );
};

export default MainInventoryList;
